import React from 'react';
import { Download, PenTool, Image } from 'lucide-react';

interface PatentDrawingDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showHeader?: boolean;
}

const PatentDrawingDisplay: React.FC<PatentDrawingDisplayProps> = ({
  result,
  theme = 'light',
  showHeader = true,
}) => {
  const isDark = theme === 'dark';

  let parsed = result;
  if (typeof result === 'string') {
    try { parsed = JSON.parse(result); } catch { return null; }
  }

  const data = parsed?.output || parsed;
  if (!data?.download_url) return null;

  const downloadUrl: string = data.download_url;
  const inputDownloadUrl: string | undefined = data.input_download_url;
  const instruction: string = data.instruction || '';

  const btnCls = `inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
    isDark
      ? 'bg-blue-600 hover:bg-blue-500 text-white'
      : 'bg-blue-600 hover:bg-blue-700 text-white'
  }`;

  const outlineBtnCls = `inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
    isDark
      ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
  }`;

  return (
    <div className="space-y-4">
      {showHeader && (
        <h3 className={`font-semibold text-sm flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
          <PenTool className="w-4 h-4 text-blue-500" />
          Generated Patent Drawing
        </h3>
      )}

      {instruction && (
        <div className={`rounded-lg p-3 text-sm ${isDark ? 'bg-gray-700/50 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
          <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Instruction: </span>
          {instruction}
        </div>
      )}

      {/* Generated drawing preview */}
      <div className={`rounded-lg overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <img
          src={downloadUrl}
          alt="Generated patent drawing"
          className="w-full object-contain max-h-[600px]"
        />
      </div>

      {/* Download buttons */}
      <div className="flex flex-wrap gap-2">
        <a
          href={downloadUrl}
          download="patent_drawing.png"
          target="_blank"
          rel="noopener noreferrer"
          className={btnCls}
        >
          <Download className="w-4 h-4" />
          Download Drawing (PNG)
        </a>

        {inputDownloadUrl && (
          <a
            href={inputDownloadUrl}
            download="original_image"
            target="_blank"
            rel="noopener noreferrer"
            className={outlineBtnCls}
          >
            <Image className="w-4 h-4" />
            Download Original Image
          </a>
        )}
      </div>
    </div>
  );
};

export default PatentDrawingDisplay;
